import { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Alert, CircularProgress, Divider,
  ToggleButton, ToggleButtonGroup, Table, TableHead, TableBody, TableRow,
  TableCell, Chip, useMediaQuery, useTheme,
} from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TERM_LABEL = { 1: 'Term 1', 2: 'Term 2', 3: 'Term 3' };

export default function StaffTimetable() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [data,    setData]    = useState(null);
  const [viewKey, setViewKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/timetable/my`, { headers: hdrs() });
        setData(data);
        if (data.views?.length) setViewKey(data.views[0].key);
      } catch (err) {
        setError(err.response?.data?.error || 'Could not load your timetable.');
      } finally { setLoading(false); }
    })();
  }, []);

  const view = data?.views?.find(v => v.key === viewKey) || null;
  const isPersonal = viewKey === 'personal';

  // Lesson periods only (skip breaks/lunch/games), ordered by periodNo.
  const lessonPeriods = useMemo(() => {
    const periods = data?.config?.periods || [];
    return periods.filter(p => p.type === 'lesson').sort((a, b) => a.periodNo - b.periodNo);
  }, [data]);

  const days = data?.config?.daysOfWeek || [];

  // Map "day-periodNo" → entry for the selected view.
  const cellMap = useMemo(() => {
    const m = {};
    (view?.entries || []).forEach(e => { m[`${e.day}-${e.periodNo}`] = e; });
    return m;
  }, [view]);

  const secondary = (e) => isPersonal ? (e.classes || []).join(', ') : e.teacher;
  const periodLabel = (p) => p.lessonNo ? `L${p.lessonNo}` : `P${p.periodNo}`;

  if (loading) return (
    <Box sx={{ textAlign: 'center', mt: 8 }}><CircularProgress /><Typography mt={2}>Loading…</Typography></Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: { xs: 1.5, sm: 4 }, mb: 4, px: { xs: 1.5, sm: 2 } }}>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
            <CalendarMonth sx={{ color: '#00838f' }} />
            <Typography variant="h6" fontWeight={700}>My Timetable</Typography>
            {data?.published && (
              <Chip size="small" variant="outlined"
                label={`${data.academicYear} · ${TERM_LABEL[data.term] || `Term ${data.term}`}`} sx={{ ml: 0.5 }} />
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {!error && data && !data.published && (
            <Alert severity="info">No published timetable yet. It will appear here once the school publishes it.</Alert>
          )}

          {data?.published && (!data.views || data.views.length === 0) && (
            <Alert severity="info">You have no lessons or classes assigned for this term.</Alert>
          )}

          {view && (
            <>
              {data.views.length > 1 && (
                <ToggleButtonGroup exclusive size="small" value={viewKey}
                  onChange={(_, v) => v && setViewKey(v)} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  {data.views.map(v => (
                    <ToggleButton key={v.key} value={v.key} sx={{ textTransform: 'none' }}>{v.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}

              {isMobile ? (
                /* ── Phone: grouped by day, lessons only ── */
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {days.map(day => {
                    const dayLessons = lessonPeriods
                      .map(p => ({ p, e: cellMap[`${day}-${p.periodNo}`] }))
                      .filter(x => x.e);
                    return (
                      <Card key={day} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography fontWeight={700} color="primary" gutterBottom>{day}</Typography>
                          {dayLessons.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">No lessons</Typography>
                          ) : dayLessons.map(({ p, e }) => (
                            <Box key={p.periodNo} display="flex" gap={1.2} py={0.6}
                              sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
                              <Box sx={{ minWidth: 76 }}>
                                <Typography variant="caption" fontWeight={700}>{periodLabel(p)}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">{p.startTime}–{p.endTime}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{e.subject || '—'}</Typography>
                                {secondary(e) && <Typography variant="caption" color="text.secondary">{secondary(e)}</Typography>}
                              </Box>
                            </Box>
                          ))}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              ) : (
                /* ── Desktop: weekly grid ── */
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 640, '& td, & th': { border: '1px solid', borderColor: 'divider' } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ width: 96 }}><strong>Period</strong></TableCell>
                        {days.map(d => <TableCell key={d} align="center"><strong>{d}</strong></TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lessonPeriods.map(p => (
                        <TableRow key={p.periodNo}>
                          <TableCell sx={{ bgcolor: 'grey.50' }}>
                            <Typography variant="caption" fontWeight={700} display="block">{periodLabel(p)}</Typography>
                            <Typography variant="caption" color="text.secondary">{p.startTime}–{p.endTime}</Typography>
                          </TableCell>
                          {days.map(day => {
                            const e = cellMap[`${day}-${p.periodNo}`];
                            return (
                              <TableCell key={day} align="center" sx={{ verticalAlign: 'top', bgcolor: e ? '#00838f0d' : 'inherit' }}>
                                {e ? (
                                  <>
                                    <Typography variant="body2" fontWeight={600} lineHeight={1.2}>{e.subject || '—'}</Typography>
                                    {secondary(e) && <Typography variant="caption" color="text.secondary">{secondary(e)}</Typography>}
                                  </>
                                ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
